// adminfrontend/src/admin_ui/pages/AdminLocationsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function AdminLocationsPage({ adminEmail, onLogout }) {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "warehouse",
  });

  const selectedBranch = useMemo(
    () => branches.find((b) => String(b.id) === String(branchId)) || null,
    [branches, branchId]
  );

  async function loadBranches() {
    const data = await adminInventoryApi.listBranches();
    if (!data || data.ok !== true) throw new Error("branches_failed");
    const bs = data.branches || [];
    setBranches(bs);
    if (!branchId && bs[0]) setBranchId(String(bs[0].id));
    return bs;
  }

  async function loadLocations(bId) {
    if (!bId) {
      setLocations([]);
      return;
    }
    const data = await adminInventoryApi.listLocations(bId);
    if (!data || data.ok !== true) throw new Error("locations_failed");
    setLocations(data.locations || []);
  }

  async function fullLoad() {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const bs = await loadBranches();
      const effectiveBranchId = branchId || (bs[0] ? String(bs[0].id) : "");
      if (effectiveBranchId) await loadLocations(effectiveBranchId);
    } catch (e) {
      console.error("[AdminLocationsPage] load error:", e);
      setError("Unable to load branches/locations. Check login or server.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fullLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setError("");
    setInfo("");
    loadLocations(branchId).catch((e) => {
      console.error("[AdminLocationsPage] locations error:", e);
      setError("Unable to load locations.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  async function onSubmit(e) {
    e.preventDefault();
    setActionLoading(true);
    setError("");
    setInfo("");
    try {
      const payload = {
        branchId: Number(branchId),
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        type: form.type,
      };
      const res = await adminInventoryApi.createLocation(payload);
      if (!res || res.ok !== true) {
        setError("Failed to create location.");
        return;
      }
      setInfo("Location created successfully.");
      setForm({ name: "", code: "", type: "warehouse" });
      await loadLocations(branchId);
    } catch (e) {
      console.error("[AdminLocationsPage] create error:", e);
      setError("Unable to create location (check branch + uniqueness).");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Stock Locations</h1>
            <p className="ucx-admin-subtitle">
              Manage stock locations under a branch (warehouse, storefront, transit).
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}

            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={fullLoad}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button
                type="button"
                className="ucx-admin-btn ucx-admin-btn-reject"
                onClick={onLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Location</div>

          <div className="ucx-admin-toolbar">
            <div className="ucx-admin-field ucx-admin-field-inline">
              <label>Branch</label>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} {b.code ? `(${b.code})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="ucx-admin-toolbar-hint">
              Selected: <b>{selectedBranch ? selectedBranch.name : "—"}</b>
            </div>
          </div>

          <form className="ucx-admin-form" onSubmit={onSubmit}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Main Warehouse"
                  minLength={2}
                  required
                />
              </div>

              <div className="ucx-admin-field">
                <label>Code (optional)</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                  placeholder="e.g. MAIN-WH"
                />
              </div>

              <div className="ucx-admin-field">
                <label>Type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                >
                  <option value="warehouse">warehouse</option>
                  <option value="storefront">storefront</option>
                  <option value="transit">transit</option>
                </select>
              </div>
            </div>

            <div className="ucx-admin-form-actions">
              <button
                type="submit"
                className="ucx-admin-btn ucx-admin-btn-approve"
                disabled={!branchId || actionLoading}
              >
                {actionLoading ? "Working…" : "Create Location"}
              </button>
            </div>
          </form>

          {!!error && <div className="ucx-admin-status-error">{error}</div>}
          {!!info && <div className="ucx-admin-status-success">{info}</div>}
        </div>

        <div className="ucx-admin-status">
          {!loading && !error && locations.length === 0 && (
            <div className="ucx-admin-empty">
              No locations found for this branch.
            </div>
          )}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Branch</th>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.id}>
                  <td>{l.id}</td>
                  <td>{selectedBranch ? selectedBranch.name : l.branch_id}</td>
                  <td>{l.name}</td>
                  <td>{l.code || "-"}</td>
                  <td>{l.type || "-"}</td>
                  <td>{l.status || "-"}</td>
                  <td>{l.created_at ? new Date(l.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    Loading locations…
                  </td>
                </tr>
              )}

              {!loading && locations.length === 0 && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    No locations yet for this branch.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminLocationsPage;
